import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'

const FADE_MS = 2500

const positionKey = (p: { left: string; top: string }) => {
  return `${p.left}|${p.top}`
}

const pickNextPosition = (
  pool: readonly { left: string; top: string }[],
  current: { left: string; top: string },
): { left: string; top: string } => {
  if (pool.length <= 1) return current
  let next = pool[Math.floor(Math.random() * pool.length)]!
  let guard = 0
  while (positionKey(next) === positionKey(current) && guard++ < 32) {
    next = pool[Math.floor(Math.random() * pool.length)]!
  }
  return next
}

const randomMoveDurationMs = (
  baseDuration: number,
  containerMaxSidePx: number,
  referencePx: number,
) => {
  const scale =
    containerMaxSidePx > 0 && referencePx > 0
      ? containerMaxSidePx / referencePx
      : 1
  return baseDuration + Math.random() * 14000 * scale
}

type Phase = 'moving' | 'fadingOut' | 'fadingInPending' | 'fadingIn'

type DriftingBlobProps = {
  color: string
  pool: readonly { left: string; top: string }[]
  /** Stable index for deterministic first paint (hydration-safe) */
  seedIndex: number
  reduceMotion: boolean
  className?: string
  moveDurationBaseMs: number
  containerMaxSidePx: number
  durationScaleReferencePx: number
}

const DriftingBlob = ({
  color,
  pool,
  seedIndex,
  reduceMotion,
  className,
  moveDurationBaseMs,
  containerMaxSidePx,
  durationScaleReferencePx,
}: DriftingBlobProps) => {
  const [pos, setPos] = useState<{ left: string; top: string }>(
    () => pool[seedIndex % pool.length]!,
  )
  const [durationMs, setDurationMs] = useState(12000)
  const [phase, setPhase] = useState<Phase>('moving')

  const phaseRef = useRef(phase)
  const durationScaleRef = useRef({ containerMaxSidePx, durationScaleReferencePx })

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  useEffect(() => {
    durationScaleRef.current = { containerMaxSidePx, durationScaleReferencePx }
  }, [containerMaxSidePx, durationScaleReferencePx])

  useEffect(() => {
    if (!reduceMotion) return
    const id = requestAnimationFrame(() => {
      setPhase('moving')
      setPos(pool[seedIndex % pool.length]!)
      setDurationMs(12000)
    })
    return () => cancelAnimationFrame(id)
  }, [reduceMotion, pool, seedIndex])

  useEffect(() => {
    if (reduceMotion) return
    const id = requestAnimationFrame(() => {
      if (phaseRef.current !== 'moving') return
      setPos((p) => pickNextPosition(pool, p))
      const { containerMaxSidePx: maxSide, durationScaleReferencePx: refPx } =
        durationScaleRef.current
      setDurationMs(randomMoveDurationMs(moveDurationBaseMs, maxSide, refPx))
    })
    return () => cancelAnimationFrame(id)
  }, [reduceMotion, pool, moveDurationBaseMs])

  useLayoutEffect(() => {
    if (reduceMotion) return
    if (phase !== 'fadingInPending') return
    const id = requestAnimationFrame(() => {
      setPhase('fadingIn')
    })
    return () => cancelAnimationFrame(id)
  }, [reduceMotion, phase])

  const onTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (reduceMotion) return

      if (phase === 'moving' && e.propertyName === 'left') {
        setPhase('fadingOut')
        return
      }
      if (phase === 'fadingOut' && e.propertyName === 'opacity') {
        setPos((p) => pickNextPosition(pool, p))
        setPhase('fadingInPending')
        return
      }
      if (phase === 'fadingIn' && e.propertyName === 'opacity') {
        setDurationMs(
          randomMoveDurationMs(
            moveDurationBaseMs,
            containerMaxSidePx,
            durationScaleReferencePx,
          ),
        )
        setPos((p) => pickNextPosition(pool, p))
        setPhase('moving')
      }
    },
    [
      reduceMotion,
      pool,
      moveDurationBaseMs,
      containerMaxSidePx,
      durationScaleReferencePx,
      phase,
    ],
  )

  const motionStyle =
    reduceMotion || phase === 'fadingInPending'
      ? {}
      : phase === 'moving'
        ? {
            transitionProperty: 'left, top' as const,
            transitionDuration: `${durationMs}ms`,
            transitionTimingFunction: 'ease-in-out' as const,
          }
        : {
            transitionProperty: 'opacity' as const,
            transitionDuration: `${FADE_MS}ms`,
            transitionTimingFunction: 'ease-in-out' as const,
          }

  const opacity = reduceMotion || phase === 'moving' || phase === 'fadingIn' ? 1 : 0

  return (
    <div
      className={cn(
        'absolute size-[40vmax] -translate-1/2 rounded-full md:size-[60vmax]',
        className,
      )}
      style={{
        left: pos.left,
        top: pos.top,
        background: color,
        opacity,
        ...(reduceMotion ? {} : phase === 'fadingInPending' ? { transition: 'none' } : motionStyle),
      }}
      onTransitionEnd={onTransitionEnd}
    />
  )
}

export default DriftingBlob
